import type { AccountInfo } from '@azure/msal-browser'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { msalInstance, apiScopes, isEntraConfigured } from '../auth'

/**
 * The one place this app turns credentials into request headers.
 *
 * It exists because there were five copies — `api.ts`, `auditLogApi.ts`, `dataEntryApi.ts`,
 * `dataEntryGridApi.ts` and `signalr.ts` each carried a byte-identical `getApiKey`/`getBearerToken`/
 * `createHeaders` trio (two of those five went with the Data Entry removal). Five copies meant five *separate* in-flight windows, so deduplicating token
 * acquisition per module would not have deduplicated anything: the dashboard's opening burst spans
 * modules (activity, runs, health, entities), which is precisely the case worth collapsing.
 */

/** The API key: Aspire's build-time env var first, then whatever the user typed into the UI. */
export function getApiKey(): string | null {
  const envApiKey = import.meta.env.VITE_API_KEY
  if (envApiKey) return envApiKey
  return localStorage.getItem('lighthouse-api-key')
}

/**
 * In-flight token acquisition, shared by concurrent callers.
 *
 * MSAL caches the *token* but not the work of fetching it: every `acquireTokenSilent` re-ran the whole
 * silent flow and emitted a dozen log lines, which is where the wall of `@azure/msal-common` output in
 * the console came from. One shared promise per burst collapses that to a single acquisition.
 *
 * Sound only because every caller here wants the same thing — `apiScopes.read` for this API. A second
 * audience must never join: `useProfilePhoto` asks for Graph's `User.Read`, and an unkeyed promise
 * would hand it an API token that Graph rejects.
 */
let inFlightToken: Promise<string | null> | null = null

/**
 * A Bearer token for the API, or null when Entra is not configured or the silent acquisition needs
 * interaction — callers treat null as "not authenticated yet" rather than an error.
 */
export async function getBearerToken(): Promise<string | null> {
  if (!isEntraConfigured() || !msalInstance) return null

  const account = msalInstance.getActiveAccount()
  if (!account) return null

  // Deliberately not cached beyond the in-flight window: MSAL owns token lifetime and expiry, and a
  // second cache here would eventually serve an expired token with great confidence.
  inFlightToken ??= acquireToken(account)

  try {
    return await inFlightToken
  } finally {
    inFlightToken = null
  }
}

async function acquireToken(account: AccountInfo): Promise<string | null> {
  try {
    const response = await msalInstance!.acquireTokenSilent({
      scopes: [apiScopes.read].filter(Boolean),
      account,
    })
    return response.accessToken
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) return null
    console.error('Token acquisition failed:', error)
    return null
  }
}

/**
 * Auth headers for an API call: Bearer when Entra has a token, else the API key.
 *
 * Bearer wins because it carries a real user identity; the API key is a shared secret that identifies
 * only the app. The API's MultiAuth scheme dispatches on whichever header arrives.
 */
export async function createHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' }

  const bearerToken = await getBearerToken()
  if (bearerToken) {
    headers['Authorization'] = `Bearer ${bearerToken}`
    return headers
  }

  const apiKey = getApiKey()
  if (apiKey) {
    headers['X-API-Key'] = apiKey
  }

  return headers
}

/**
 * Bearer-only headers, or null when there is no Entra token. NEVER falls back to the API key.
 *
 * For every Observatory write — requesting a run, clearing a halt, handling a signal, cancelling a run,
 * acknowledging or restoring a Data Comb finding — and the signal assignee list, which the API pins to
 * JwtBearer (EtlWriteAuthorization, doc 17 amendment A4): they refuse the shared key with a 401 in every
 * environment, Development included, because each is recorded against the person who did it. Sending
 * the key anyway would only earn that 401, after putting the shared secret on a request that cannot use
 * it. Null tells the caller to refuse the action before anything is sent.
 */
export async function createBearerHeaders(): Promise<HeadersInit | null> {
  const bearerToken = await getBearerToken()
  if (!bearerToken) return null
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${bearerToken}` }
}
